/**
 * Template Normalization Utilities
 *
 * This module handles inconsistencies in template data structures between different API endpoints.
 * Some endpoints return `template_id`, others return `id`. Some use `video_name`, others use `name`.
 * This utility normalizes all variations into a consistent interface.
 */

import type { Phase, Region } from '@/types/api';
import type { TemplateMetadata } from '@/services/templateApi';
import { VIDEO_CONSTRAINTS } from '@/utils/constants';

/**
 * The union of every field `normalizeTemplate()` reads across its two real
 * producer shapes — `MovementTemplate` (types/api.ts, from
 * services/phaseApi.ts's `/phases/templates*` family) and `AnnotationTemplate`
 * (services/templateApi.ts, from `/templates*`) — plus the legacy field-name
 * aliases (`template_id`/`video_name`/`movement_name`/`sport_type`/`video_id`)
 * some producers use instead of the canonical names. All fields are optional
 * because a given input only ever populates one shape's subset; this is a
 * genuine dual/legacy-shape API boundary being normalized, not a stand-in
 * for `any` — every field the function body touches is declared here, so a
 * typo or a removed upstream field still gets caught.
 */
export interface RawTemplateInput {
  template_id?: string;
  id?: string;
  video_name?: string;
  name?: string;
  movement_name?: string;
  movement_type?: string;
  sport_type?: string;
  description?: string;
  reference_video_id?: string;
  video_id?: string;
  phases?: Phase[];
  regions?: Region[];
  annotations?: unknown[];
  total_frames?: number;
  fps?: number;
  duration_seconds?: number;
  trim_start_frame?: number;
  trim_end_frame?: number;
  created_at?: string;
  updated_at?: string;
  finalized?: boolean;
  finalized_at?: string;
  finalized_by?: string;
  coach_notes?: string;
  metadata?: TemplateMetadata;
}

/**
 * Normalized template interface with consistent property names
 * This is what components should use internally after normalization
 */
export interface NormalizedTemplate {
  // Core identifiers - always present
  id: string;
  name: string;

  // Movement/sport information
  movementType: string;
  sportType?: string;
  description?: string;

  // Video references
  referenceVideoId: string;
  videoId?: string; // Some endpoints use this instead

  // Template content
  phases: Phase[];
  regions: Region[];
  // Raw annotations from backend — left as `unknown[]` (not the narrower
  // canvas or types/api `Annotation[]`) because `normalizeTemplate` accepts
  // either producer shape and passes this field through unchanged; callers
  // that need concrete fields (TemplateDetails.tsx) do their own defensive
  // per-field extraction across both shapes' field names.
  annotations?: unknown[];

  // Video metadata
  totalFrames: number;
  fps: number;
  duration: number; // in seconds
  trimStartFrame?: number;
  trimEndFrame?: number;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;

  // Finalization status
  finalized: boolean;
  finalizedAt?: string;
  finalizedBy?: string;

  // Additional metadata
  coachNotes?: string;
  metadata?: TemplateMetadata;
}

/**
 * Normalizes a template object from any API response format into a consistent structure
 *
 * Handles these variations:
 * - template_id vs id
 * - video_name vs name
 * - movement_name vs movement_type
 * - reference_video_id vs video_id
 * - total_frames in root vs in metadata
 * - fps in root vs in metadata
 *
 * @param template - Raw template object from API (either `MovementTemplate`
 *   or `AnnotationTemplate` shape, possibly using legacy field-name aliases)
 * @returns Normalized template with consistent property names
 */
export function normalizeTemplate(template: RawTemplateInput): NormalizedTemplate {
  // Safely extract core identifiers
  const id = template.template_id || template.id || '';
  const name = template.video_name || template.name || 'Unnamed Template';

  // Movement and sport information
  const movementType = template.movement_name || template.movement_type || 'Unknown';
  const sportType = template.sport_type;
  const description = template.description;

  // Video references - try all possible field names
  const referenceVideoId = template.reference_video_id || template.video_id || '';
  const videoId = template.video_id || template.reference_video_id;

  // Template content
  const phases = template.phases || [];
  const regions = template.regions || [];
  const annotations = template.annotations;

  // Video metadata - check both root level and nested metadata
  const totalFrames = template.total_frames || template.metadata?.total_frames || 0;
  const fps = template.fps || template.metadata?.fps || VIDEO_CONSTRAINTS.DEFAULT_FPS;
  const durationSeconds = template.duration_seconds || template.metadata?.duration_seconds || 0;
  const duration = totalFrames > 0 && fps > 0 ? totalFrames / fps : durationSeconds;

  // Trimming information
  const trimStartFrame = template.trim_start_frame;
  const trimEndFrame = template.trim_end_frame;

  // Timestamps
  const createdAt = template.created_at;
  const updatedAt = template.updated_at;

  // Finalization status
  const finalized = template.finalized || false;
  const finalizedAt = template.finalized_at;
  const finalizedBy = template.finalized_by;

  // Additional metadata
  const coachNotes = template.coach_notes;
  const metadata = template.metadata;

  return {
    id,
    name,
    movementType,
    sportType,
    description,
    referenceVideoId,
    videoId,
    phases,
    regions,
    annotations,
    totalFrames,
    fps,
    duration,
    trimStartFrame,
    trimEndFrame,
    createdAt,
    updatedAt,
    finalized,
    finalizedAt,
    finalizedBy,
    coachNotes,
    metadata,
  };
}

/**
 * Checks if a template has the minimum required data to be valid
 *
 * @param template - Normalized template to validate
 * @returns Object with isValid boolean and array of missing fields
 */
export function validateTemplate(template: NormalizedTemplate): {
  isValid: boolean;
  missingFields: string[];
  errors: string[];
} {
  const missingFields: string[] = [];
  const errors: string[] = [];

  // Check required fields
  if (!template.id) missingFields.push('id');
  if (!template.name || template.name === 'Unnamed Template') missingFields.push('name');
  if (!template.referenceVideoId) missingFields.push('referenceVideoId');
  if (!template.movementType || template.movementType === 'Unknown') missingFields.push('movementType');

  // Check for content
  if (!template.phases || template.phases.length === 0) {
    errors.push('Template must have at least one phase defined');
  }

  // Check for tracked regions - skip if Global Body Matching is set to 100%
  const globalBodyMatchEnabled = template.metadata?.global_body_match_enabled;
  const globalBodyMatchWeight = template.metadata?.global_body_match_weight ?? 0;
  const isFullBodyMatchOnly = globalBodyMatchEnabled && globalBodyMatchWeight >= 1.0;

  if (!isFullBodyMatchOnly && (!template.regions || template.regions.length === 0)) {
    errors.push('Template must have at least one tracked region');
  }

  // Check video metadata
  if (!template.totalFrames || template.totalFrames === 0) {
    errors.push('Template must have valid video metadata (total frames)');
  }

  return {
    isValid: missingFields.length === 0 && errors.length === 0,
    missingFields,
    errors,
  };
}

/**
 * Formats template duration as a human-readable string
 *
 * @param template - Normalized template
 * @returns Formatted duration string (e.g., "2.5s", "1m 30s")
 */
export function formatTemplateDuration(template: NormalizedTemplate): string {
  const seconds = template.duration;

  if (seconds < 60) {
    return `${seconds.toFixed(2)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Gets the display name for a template (handles fallbacks gracefully)
 *
 * @param template - Normalized template
 * @returns Display name for UI
 */
export function getTemplateDisplayName(template: NormalizedTemplate): string {
  return template.name !== 'Unnamed Template'
    ? template.name
    : `${template.movementType} Template`;
}

/**
 * Gets the full movement description (combines sport and movement type)
 *
 * @param template - Normalized template
 * @returns Full movement description
 */
export function getTemplateMovementDescription(template: NormalizedTemplate): string {
  if (template.sportType) {
    return `${template.sportType} - ${template.movementType}`;
  }
  return template.movementType;
}

/**
 * Checks if a template is ready for finalization
 *
 * @param template - Normalized template
 * @returns Validation result with any blocking issues
 */
export function canFinalizeTemplate(template: NormalizedTemplate): {
  canFinalize: boolean;
  reasons: string[];
} {
  const validation = validateTemplate(template);
  const reasons: string[] = [];

  if (!validation.isValid) {
    reasons.push(...validation.errors);

    if (validation.missingFields.includes('name')) {
      reasons.push('Template name is required');
    }
    if (validation.missingFields.includes('movementType')) {
      reasons.push('Movement type is required');
    }
    if (validation.missingFields.includes('referenceVideoId')) {
      reasons.push('Template must have a reference video');
    }
  }

  // Already finalized
  if (template.finalized) {
    reasons.push('Template is already finalized');
  }

  return {
    canFinalize: reasons.length === 0,
    reasons,
  };
}
